import {NodeElement, type NodeElementOptions, type StyleObject} from "./NodeElement";
import {PanelElement} from "./PanelElement";
import {type PanelOptions} from "../../PanelOptionsLogic";
import {IFrameMessages} from "../../messaging/IFrameMessages";
import {GATE_MOUNT_TYPE} from "../../Constants";
import {bodyScrollLock} from "../../utils/ScrimHelpers.js";

// How often a mounted gate checks that it's still on the page.
const DISCARD_CHECK_INTERVAL_MS = 200;

// The gates on the page, so one that replaces a container's children can find ours in there.
const mountedGates = new Set<GateElement>();

export interface InteractiveHandlers {
    remove: () => void;
    updatePanel: (panelOptions: Partial<NodeElementOptions>) => void;
    // True once the merchant's router took the gate off the page without us tearing it down.
    isDiscarded: () => boolean;
    // True once another of our gates replaced this one in its container.
    isReplaced: () => boolean;
}

export const GateStyle = {
    gate: {
        pointerEvents: "initial",
        height: "100%",
        display: "flex"
    },
};

// What a gate is given beyond a plain node's options. The three are the panel options of the same name,
// read below; everything else a caller passes rides NodeElementOptions' index signature, as it did before
export interface GateElementOptions extends NodeElementOptions,
    Partial<Pick<PanelOptions, "customStyle" | "gateType" | "mountType">> {}

export class GateElement extends NodeElement {
    declare options: GateElementOptions;

    styleSheet = GateStyle;
    panel: PanelElement;
    parentNode: HTMLElement;
    initialParentStyle: Record<string, string>;
    // Set by the gates that occlude the viewport. Banners and previews leave the page scrollable on
    // purpose - the reader still has an article to finish, or a footer to reach.
    locksBodyScroll = false;
    // Set when the merchant's router took our node off the page. Distinct from a gate we tore down
    // ourselves: a dismissed gate has to stay gone, a discarded one has to be replaced on the new page.
    discarded = false;
    // Set when another of our gates took over the container. Its journey may fill other containers, not this one.
    replaced = false;

    constructor(options: GateElementOptions, panel: PanelElement) {
        super(options);
        this.panel = panel;
    }

    // An SPA router can drop our node without going through destroyNode, and then nothing releases the
    // scroll lock or frees up the container. So we watch for it ourselves and tear down through the normal
    // path. Returns whether we're still up.
    checkStillMounted(): boolean {
        if (this.node && !document.contains(this.node)) {
            this.discarded = true;
            this.destroyNode();
        }
        return !!this.node;
    }

    refreshStyle(): void {
        super.refreshStyle();
        this.setStyle(this.styleSheet.gate);
        Object.assign(this.parentNode.style, this.computeParentStyle());
        this.panel.updateOptions({style: this.getPanelStyle()});
        this.setStyle(this.options.customStyle ?? {});
    }

    getPanelStyle(): StyleObject {
        return {
            maxWidth: "100%",
            minWidth: "100%",
            border: 0,
            height: 0,
        };
    }

    computeParentStyle(): StyleObject {
        return {};
    }

    mount(parentNode: HTMLElement) {
        if (this.options.mountType === GATE_MOUNT_TYPE.replaceChildren) {
            // Ours in there are replaced, not discarded: a discarded one would be put back, wiping this one in turn.
            // Done first, so the parent style recorded below is free of theirs.
            for (const gate of mountedGates) {
                if (parentNode.contains(gate.node)) {
                    gate.replaced = true;
                    gate.destroyNode();
                }
            }
        }

        this.initialParentStyle = {};
        for (const key of Array.from(parentNode.style)) {
            this.initialParentStyle[key] = parentNode.style.getPropertyValue(key);
        }

        this.parentNode = parentNode;
        if (this.options.mountType === GATE_MOUNT_TYPE.replaceChildren) {
            parentNode.innerHTML = "";
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.firstChild) {
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.lastChild) {
            super.mount(parentNode, parentNode.childNodes.length);
        }

        if (this.options.gateType) {
            this.node.classList.add("blink-" + this.options.gateType);
        }

        this.panel.mount(this.node);
        mountedGates.add(this);
        this.addCleanupJob(() => mountedGates.delete(this));

        if (this.locksBodyScroll) {
            // Our own teardown releases the lock, since the handle is a RemoveHandle, and a discard is
            // one of our teardowns.
            this.addCleanupJob(bodyScrollLock.acquire());
        }
    }

    destroyNode(): void {
        this.panel.destroyNode();
        const revertedParentStyle: Record<string, string> = {};
        for (const key of Object.keys(this.computeParentStyle())) {
            revertedParentStyle[key] = "";
        }
        Object.assign(this.parentNode.style, revertedParentStyle, this.initialParentStyle);
        super.destroyNode();
    }

    getInteractiveHandlers(): InteractiveHandlers {
        return {
            remove: () => {
                // Nothing to say when it's already gone - a gate the buyer dismissed gets removed again
                // by whoever mounted it, once their own reason for it goes away.
                if (this.node) {
                    this.destroyNode();
                }
            },
            updatePanel: (panelOptions: Partial<NodeElementOptions>) => {
                if (!this.panel) {
                    console.error("Panel has already been destroyed.");
                    return;
                }
                this.panel.updateOptions(panelOptions);
            },
            // Checks now rather than at the next interval, so whoever put it up can replace it right away
            isDiscarded: () => {
                this.checkStillMounted();
                return this.discarded;
            },
            isReplaced: () => this.replaced,
        }
    }

    onMount(): void {
        super.onMount();
        this.attachInterval(() => this.checkStillMounted(), DISCARD_CHECK_INTERVAL_MS);
        this.panel.sizeChange.addListener(() => this.refreshStyle());
        this.panel.addSdkListener(IFrameMessages.DESTROY_PANEL, () => {
            this.destroyNode();
        });

        this.attachEventListener(document, "visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.refreshStyle();
            }
        });
    }
}
